/// <reference types="@types/google.maps" />

export type PickedAddress = { address: string; postcode: string };

const longNameOf = (
  place: google.maps.places.PlaceResult,
  type: string,
): string | undefined =>
  place.address_components?.find((component) => component.types.includes(type))
    ?.long_name;

const TYPED_HOUSE =
  /^\s*((?:flat|apartment|unit|room)\s+[\w-]+)?,?\s*(?:(\d+[a-z]?(?:-\d+[a-z]?)?)\s)?/i;

/** The flat and house number at the start of what was typed, if any. */
const typedHouse = (
  typed: string,
): { flat: string | undefined; number: string | undefined } => {
  const match = TYPED_HOUSE.exec(typed);
  return { flat: match?.[1], number: match?.[2] };
};

/** Whether a typed house number adds a letter or a range to Google's. */
const refines = (typed: string, google: string): boolean =>
  typed.toLowerCase().startsWith(google.toLowerCase()) &&
  /^[a-z-]/i.test(typed.slice(google.length));

const houseNumber = (
  google: string | undefined,
  typed: string | undefined,
  isStreet: boolean,
): string | undefined => {
  if (typed === undefined) {
    return google;
  }
  if (google === undefined) {
    return isStreet ? typed : undefined;
  }
  return refines(typed, google) ? typed : google;
};

/**
 * Writes a chosen place as a UK postal address: the lines above the postcode,
 * then the postcode on its own. County and country are left out because the
 * postcode already says where in the UK it is.
 *
 * `typed` is what was in the field when the place was chosen. Google often
 * knows a street but not the house on it, a house but not the letter or range
 * of its number, or a house but not the flat within it. What was typed is kept
 * where Google has less, except a house number for a place that is not a
 * street, which would make an address that does not exist.
 */
export const addressFromPlace = (
  place: google.maps.places.PlaceResult,
  typed = "",
): PickedAddress => {
  const typedParts = typedHouse(typed);
  const isStreet = place.types?.includes("route") ?? false;

  const street = [
    houseNumber(
      longNameOf(place, "street_number"),
      typedParts.number,
      isStreet,
    ),
    longNameOf(place, "route"),
  ]
    .filter((part) => part !== undefined)
    .join(" ");

  const lines = [
    // Other places' names only restate the street.
    place.types?.includes("establishment") ? place.name : undefined,
    longNameOf(place, "subpremise") ?? typedParts.flat,
    longNameOf(place, "premise"),
    street === "" ? undefined : street,
    longNameOf(place, "postal_town"),
  ].filter((line) => line !== undefined);

  return {
    address: [...new Set(lines)].join(", "),
    postcode: longNameOf(place, "postal_code") ?? "",
  };
};
